import { Locale } from '@finxone-platform/shared/enums';
import { AlertTypes } from '@finxone-platform/shared/sys-config-types';
import { OnboardingIndividualErrorCodes } from '../error-codes/onboarding-individual.type';
import {
  CheckDuplicateEmailErrorCodes,
  EmailVerifyErrorCodes,
  PhoneNumberVerifyErrorCodes,
} from '../error-codes/verification.type';
import { OnboardingIndividualSuccessCodes } from '../success-codes/onboarding-individual.type';
import { defaultOnboardingIndividualTranslations } from './en-GB/onboarding-individual-translations';
import { SupportedToasterLocales } from './general.utils';

export const onboardingIndividualTranslations: {
  // For new locales just add them here to the extract
  [key in SupportedToasterLocales]: typeof defaultOnboardingIndividualTranslations;
} = {
  [Locale.en_GB]: defaultOnboardingIndividualTranslations,
  // Add more locales as needed
};

export const generateOnboardingIndividualAlerts = (
  translations: typeof defaultOnboardingIndividualTranslations,
) => ({
  [OnboardingIndividualErrorCodes.ALREADY_ASSOCIATED_USERS]: {
    name: 'User is already associated',
    type: AlertTypes.ERROR,
    title: translations.ALREADY_ASSOCIATED_USERS_TITLE,
    description: translations.ALREADY_ASSOCIATED_USERS_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.CREATING_ORG_FAILED]: {
    name: 'Creating organisation failed',
    type: AlertTypes.ERROR,
    title: translations.CREATING_ORG_FAILED_TITLE,
    description: translations.CREATING_ORG_FAILED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.DUPLICATE_EMAIL]: {
    name: 'Email is already used',
    type: AlertTypes.ERROR,
    title: translations.DUPLICATE_EMAIL_TITLE,
    description: translations.DUPLICATE_EMAIL_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.EMAIL_INVALID]: {
    name: 'Email is invalid',
    type: AlertTypes.ERROR,
    title: translations.EMAIL_INVALID_TITLE,
    description: translations.EMAIL_INVALID_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.EMAIL_REQUIRED]: {
    name: 'Email is required',
    type: AlertTypes.ERROR,
    title: translations.EMAIL_REQUIRED_TITLE,
    description: translations.EMAIL_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.EXISTING_USER]: {
    name: 'User already exists in an organisation',
    type: AlertTypes.ERROR,
    title: translations.EXISTING_USER_TITLE,
    description: translations.EXISTING_USER_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.FIRST_NAME_REQUIRED]: {
    name: 'First name required',
    type: AlertTypes.ERROR,
    title: translations.FIRST_NAME_REQUIRED_TITLE,
    description: translations.FIRST_NAME_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ONBOARD_FAILED]: {
    name: 'Onboarding failed',
    type: AlertTypes.ERROR,
    title: translations.ONBOARD_FAILED_TITLE,
    description: translations.ONBOARD_FAILED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.UNABLE_TO_ADD_USER]: {
    name: 'Unabled to add user',
    type: AlertTypes.ERROR,
    title: translations.UNABLE_TO_ADD_USER_TITLE,
    description: translations.UNABLE_TO_ADD_USER_DESCRIPTION,
  },
  [OnboardingIndividualSuccessCodes.ONBOARD_SUCCESS]: {
    name: 'Onboarding success',
    type: AlertTypes.ERROR,
    title: translations.ONBOARD_SUCCESS_TITLE,
    description: translations.ONBOARD_SUCCESS_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.EMAIL_VERIFIED_BOOLEAN]: {
    name: 'Email verified',
    type: AlertTypes.ERROR,
    title: translations.EMAIL_VERIFIED_BOOLEAN_TITLE,
    description: translations.EMAIL_VERIFIED_BOOLEAN_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.FIRST_NAME_IS_STRING]: {
    name: 'First name is string',
    type: AlertTypes.ERROR,
    title: translations.FIRST_NAME_IS_STRING_TITLE,
    description: translations.FIRST_NAME_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.LAST_NAME_REQUIRED]: {
    name: 'Last name required',
    type: AlertTypes.ERROR,
    title: translations.LAST_NAME_REQUIRED_TITLE,
    description: translations.LAST_NAME_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.LAST_NAME_IS_STRING]: {
    name: 'Last name is string',
    type: AlertTypes.ERROR,
    title: translations.LAST_NAME_IS_STRING_TITLE,
    description: translations.LAST_NAME_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PASSWORD_REQUIRED]: {
    name: 'Password required',
    type: AlertTypes.ERROR,
    title: translations.PASSWORD_REQUIRED_TITLE,
    description: translations.PASSWORD_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PASSWORD_IS_STRING]: {
    name: 'Password is string',
    type: AlertTypes.ERROR,
    title: translations.PASSWORD_IS_STRING_TITLE,
    description: translations.PASSWORD_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.COUNTRY_REQUIRED]: {
    name: 'Country required',
    type: AlertTypes.ERROR,
    title: translations.COUNTRY_REQUIRED_TITLE,
    description: translations.COUNTRY_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.COUNTRY_IS_STRING]: {
    name: 'Country is string',
    type: AlertTypes.ERROR,
    title: translations.COUNTRY_IS_STRING_TITLE,
    description: translations.COUNTRY_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.COUNTRY_IS_ISO31661ALPHA2]: {
    name: 'Country is ISO31661Alpha2',
    type: AlertTypes.ERROR,
    title: translations.COUNTRY_IS_ISO31661ALPHA2_TITLE,
    description: translations.COUNTRY_IS_ISO31661ALPHA2_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ADDRESS1_IS_STRING]: {
    name: 'Address1 is string',
    type: AlertTypes.ERROR,
    title: translations.ADDRESS1_IS_STRING_TITLE,
    description: translations.ADDRESS1_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ADDRESS2_IS_STRING]: {
    name: 'Address2 is string',
    type: AlertTypes.ERROR,
    title: translations.ADDRESS2_IS_STRING_TITLE,
    description: translations.ADDRESS2_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ADDRESS3_IS_STRING]: {
    name: 'Address3 is string',
    type: AlertTypes.ERROR,
    title: translations.ADDRESS3_IS_STRING_TITLE,
    description: translations.ADDRESS3_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ADDRESS4_IS_STRING]: {
    name: 'Address4 is string',
    type: AlertTypes.ERROR,
    title: translations.ADDRESS4_IS_STRING_TITLE,
    description: translations.ADDRESS4_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.CITY_REQUIRED]: {
    name: 'City required',
    type: AlertTypes.ERROR,
    title: translations.CITY_REQUIRED_TITLE,
    description: translations.CITY_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.CITY_IS_STRING]: {
    name: 'City is string',
    type: AlertTypes.ERROR,
    title: translations.CITY_IS_STRING_TITLE,
    description: translations.CITY_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.POSTAL_CODE_REQUIRED]: {
    name: 'Postal code required',
    type: AlertTypes.ERROR,
    title: translations.POSTAL_CODE_REQUIRED_TITLE,
    description: translations.POSTAL_CODE_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.POSTAL_CODE_IS_STRING]: {
    name: 'Postal code is string',
    type: AlertTypes.ERROR,
    title: translations.POSTAL_CODE_IS_STRING_TITLE,
    description: translations.POSTAL_CODE_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.DOB_REQUIRED]: {
    name: 'Date of birth required',
    type: AlertTypes.ERROR,
    title: translations.DOB_REQUIRED_TITLE,
    description: translations.DOB_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.DOB_IS_STRING]: {
    name: 'Date of birth is string',
    type: AlertTypes.ERROR,
    title: translations.DOB_IS_STRING_TITLE,
    description: translations.DOB_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.DOB_IS_DATE_STRING]: {
    name: 'Date of birth is date string',
    type: AlertTypes.ERROR,
    title: translations.DOB_IS_DATE_STRING_TITLE,
    description: translations.DOB_IS_DATE_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.USERNAME_IS_STRING]: {
    name: 'Username is string',
    type: AlertTypes.ERROR,
    title: translations.USERNAME_IS_STRING_TITLE,
    description: translations.USERNAME_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PROPERTY_NAME_IS_STRING]: {
    name: 'Property name is string',
    type: AlertTypes.ERROR,
    title: translations.PROPERTY_NAME_IS_STRING_TITLE,
    description: translations.PROPERTY_NAME_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PROPERTY_NUMBER_IS_STRING]: {
    name: 'Property number is string',
    type: AlertTypes.ERROR,
    title: translations.PROPERTY_NUMBER_IS_STRING_TITLE,
    description: translations.PROPERTY_NUMBER_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ORGANISATION_ID_IS_STRING]: {
    name: 'Organisation ID is string',
    type: AlertTypes.ERROR,
    title: translations.ORGANISATION_ID_IS_STRING_TITLE,
    description: translations.ORGANISATION_ID_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.ORGANISATION_ID_IS_UUID]: {
    name: 'Organisation ID is UUID',
    type: AlertTypes.ERROR,
    title: translations.ORGANISATION_ID_IS_UUID_TITLE,
    description: translations.ORGANISATION_ID_IS_UUID_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PHONE_REQUIRED]: {
    name: 'Phone required',
    type: AlertTypes.ERROR,
    title: translations.PHONE_REQUIRED_TITLE,
    description: translations.PHONE_REQUIRED_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PHONE_IS_STRING]: {
    name: 'Phone is string',
    type: AlertTypes.ERROR,
    title: translations.PHONE_IS_STRING_TITLE,
    description: translations.PHONE_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PHONE_IS_VALID]: {
    name: 'Phone is valid',
    type: AlertTypes.ERROR,
    title: translations.PHONE_IS_VALID_TITLE,
    description: translations.PHONE_IS_VALID_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PHONE_VERIFIED_BOOLEAN]: {
    name: 'Phone verified is boolean',
    type: AlertTypes.ERROR,
    title: translations.PHONE_VERIFIED_BOOLEAN_TITLE,
    description: translations.PHONE_VERIFIED_BOOLEAN_DESCRIPTION,
  },
  [EmailVerifyErrorCodes.EMAIL_VERIFIED]: {
    name: 'Email verification success',
    type: AlertTypes.SUCCESS,
    title: 'Email verified successfully',
    description: 'Your email is verified.',
  },
  [EmailVerifyErrorCodes.WRONG_EMAIL_OTP]: {
    name: 'Email verification failed - wrong OTP',
    type: AlertTypes.ERROR,
    title: 'OTP you provided is wrong',
    description: 'OTP you provided did not match with our records, please try again',
  },
  [PhoneNumberVerifyErrorCodes.PHONE_VERIFIED]: {
    name: 'Phone number verification success',
    type: AlertTypes.SUCCESS,
    title: 'Verification Success',
    description: 'Your phone number has been successfully verified.',
  },
  [PhoneNumberVerifyErrorCodes.WRONG_PHONE_OTP]: {
    name: 'Phone number verification failed - wrong OTP',
    type: AlertTypes.ERROR,
    title: translations.WRONG_PHONE_OTP_TITLE,
    description: translations.WRONG_PHONE_OTP_DESCRIPTION,
  },
  [CheckDuplicateEmailErrorCodes.EMAIL_EXISTING]: {
    name: 'Email Address Already Registered',
    type: AlertTypes.ERROR,
    title: 'Email Address Already Registered',
    description:
      'The email address provided is already registered.  Please sign in using your password, or use a different mail to sign up again.',
  },
  [OnboardingIndividualErrorCodes.INVALID_TITLE]: {
    name: 'Invalid title',
    type: AlertTypes.ERROR,
    title: 'Invalid title',
    description: 'The title you provided is invalid, please try again with a different title',
  },
  [OnboardingIndividualErrorCodes.INVALID_PRODUCT_CODE]: {
    name: 'Invalid product code',
    type: AlertTypes.ERROR,
    title: 'Invalid product code',
    description: 'The product code you provided is invalid, please try again with a different product code',
  },
  [OnboardingIndividualErrorCodes.INVALID_DEPOSIT_AMOUNT]: {
    name: 'Invalid deposit amount',
    type: AlertTypes.ERROR,
    title: 'Invalid deposit amount',
    description:
      'The deposit amount you provided is invalid, please try again with a different deposit amount',
  },
  [OnboardingIndividualErrorCodes.INVALID_SOURCE_OF_FUNDS]: {
    name: 'Invalid source of funds',
    type: AlertTypes.ERROR,
    title: 'Invalid source of funds',
    description:
      'The source of funds you provided is invalid, please try again with a different source of funds',
  },
  [OnboardingIndividualErrorCodes.INVALID_PURPOSE_OF_FUNDS]: {
    name: 'Invalid purpose of funds',
    type: AlertTypes.ERROR,
    title: 'Invalid purpose of funds',
    description:
      'The purpose of funds you provided is invalid, please try again with a different purpose of funds',
  },
  [OnboardingIndividualErrorCodes.COUNTY_IS_STRING]: {
    name: 'County is string',
    type: AlertTypes.ERROR,
    title: translations.COUNTY_IS_STRING_TITLE,
    description: translations.COUNTY_IS_STRING_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.PRODUCT_UNAVAILABLE]: {
    name: 'Product unavailable',
    type: AlertTypes.ERROR,
    title: translations.PRODUCT_UNAVAILABLE_TITLE,
    description: translations.PRODUCT_UNAVAILABLE_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.COMPANY_NOT_FOUND]: {
    name: 'Company not found',
    type: AlertTypes.ERROR,
    title: translations.COMPANY_NOT_FOUND_TITLE,
    description: translations.COMPANY_NOT_FOUND_DESCRIPTION,
  },
  [OnboardingIndividualErrorCodes.COMPANY_NOT_ACTIVE]: {
    name: 'Company not active',
    type: AlertTypes.ERROR,
    title: translations.COMPANY_NOT_ACTIVE_TITLE,
    description: translations.COMPANY_NOT_ACTIVE_DESCRIPTION,
  },
});
